/**
 * Product reviews rendered from the migrated Yotpo metafields.
 * The summary is server-rendered. This script turns review payloads
 * (JSON, or the HTML Yotpo cached in a metafield) into a paged list
 * and keeps Yotpo's own widget from painting over it.
 */
(function (factory) {
  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (typeof document === 'undefined') return;

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(function () {
    api.suppressYotpo();
    api.init(document);
  });
})(function () {
  var PAGE_SIZE = 6;

  function unwrap(value) {
    if (typeof value !== 'string') return value;
    var trimmed = value.trim();
    if (!trimmed || (trimmed.charAt(0) !== '{' && trimmed.charAt(0) !== '[')) return value;
    try {
      return JSON.parse(trimmed);
    } catch (error) {
      return value;
    }
  }

  function asNumber(value) {
    var number = parseFloat(value);
    return isNaN(number) ? 0 : number;
  }

  function plainText(value) {
    if (value == null) return '';
    var text = String(value);
    if (text.indexOf('<') !== -1 && typeof document !== 'undefined') {
      var holder = document.createElement('div');
      holder.innerHTML = text;
      text = holder.textContent || '';
    }
    return text.replace(/\s+/g, ' ').trim();
  }

  function firstString() {
    for (var i = 0; i < arguments.length; i++) {
      var value = arguments[i];
      if (value == null) continue;
      if (typeof value === 'object') continue;
      var text = plainText(value);
      if (text) return text;
    }
    return '';
  }

  function isVerified(review) {
    var flag = review.verified_buyer;
    if (flag == null) flag = review.verified;
    return flag === true || flag === 'true' || flag === 'yes' || flag === 1 || flag === '1';
  }

  function authorOf(review) {
    var user = review.user || review.reviewer || {};
    return firstString(
      user.display_name,
      user.displayName,
      user.name,
      review.author,
      review.name,
      review.reviewer_name,
      review.user_name
    );
  }

  function imagesOf(review) {
    var images = review.images_data || review.images || review.photos || [];
    if (!images || !images.length) return [];
    return images.map(function (image) {
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image.thumb_url || image.original_url || image.image_url || image.url || image.src || '';
    }).filter(Boolean).slice(0, 4);
  }

  function normalizeReview(review) {
    if (!review || typeof review !== 'object' || Array.isArray(review)) return null;

    var score = asNumber(review.score != null ? review.score : (review.rating != null ? review.rating : review.stars));
    var title = firstString(review.title, review.review_title);
    var body = firstString(review.content, review.body, review.review, review.text, review.review_body);
    var date = firstString(review.created_at, review.date, review.created, review.reviewed_at);
    var author = authorOf(review);

    if (!title && !body && !score) return null;

    return {
      score: score,
      title: title,
      body: body,
      author: author,
      date: date,
      verified: isVerified(review),
      images: imagesOf(review)
    };
  }

  function reviewsFromValue(value, depth) {
    value = unwrap(value);
    if (value == null || value === '' || depth > 4) return [];

    if (typeof value === 'string') {
      if (value.indexOf('<') !== -1 && typeof DOMParser !== 'undefined') {
        return reviewsFromHtml(value);
      }
      return [];
    }

    if (Array.isArray(value)) {
      return value.map(normalizeReview).filter(Boolean);
    }

    if (typeof value !== 'object') return [];

    var nestedKeys = ['reviews', 'review', 'items', 'data', 'results'];
    for (var i = 0; i < nestedKeys.length; i++) {
      if (value[nestedKeys[i]]) {
        var nested = reviewsFromValue(value[nestedKeys[i]], depth + 1);
        if (nested.length) return nested;
      }
    }

    if (value.response) {
      var fromResponse = reviewsFromValue(value.response, depth + 1);
      if (fromResponse.length) return fromResponse;
    }

    var single = normalizeReview(value);
    return single ? [single] : [];
  }

  function reviewsFromHtml(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var nodes = doc.querySelectorAll('.yotpo-review, [data-review-id], .yotpo-regular-box');
    var reviews = [];

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.querySelector('.yotpo-review, [data-review-id]')) continue;

      var titleNode = node.querySelector('.content-title, .yotpo-review-title');
      var bodyNode = node.querySelector('.content-review, .yotpo-review-content, .review-content');
      var authorNode = node.querySelector('.yotpo-user-name, .yotpo-reviewer-name');
      var dateNode = node.querySelector('.yotpo-review-date');
      var verifiedNode = node.querySelector('.yotpo-user-title');
      var scoreText = '';
      var scoreNode = node.querySelector('.sr-only, .yotpo-review-stars');
      if (scoreNode) scoreText = scoreNode.textContent || '';

      var score = asNumber((scoreText.match(/[0-9.]+/) || [])[0]);
      if (!score) {
        score = node.querySelectorAll('.yotpo-icon-star').length;
        if (!score) score = node.querySelectorAll('.yotpo-icon.yotpo-icon-star').length;
      }

      var review = normalizeReview({
        score: score,
        title: titleNode ? titleNode.textContent : '',
        content: bodyNode ? bodyNode.textContent : '',
        author: authorNode ? authorNode.textContent : '',
        created_at: dateNode ? dateNode.textContent : '',
        verified_buyer: verifiedNode ? /verified/i.test(verifiedNode.textContent || '') : false
      });

      if (review) reviews.push(review);
    }

    return reviews;
  }

  function dedupe(reviews) {
    var seen = {};
    return reviews.filter(function (review) {
      var key = [review.author, review.date, review.title, review.body].join('|');
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  function collectReviews(sources) {
    var structured = [];
    var fromHtml = [];

    (sources || []).forEach(function (source) {
      var value = unwrap(source);
      var isHtml = typeof value === 'string' && value.indexOf('<') !== -1;
      var bucket = isHtml ? fromHtml : structured;
      reviewsFromValue(value, 0).forEach(function (review) {
        bucket.push(review);
      });
    });

    return dedupe(structured.length ? structured : fromHtml);
  }

  function distributionFrom(sources) {
    var distribution = null;
    (sources || []).forEach(function (source) {
      if (distribution) return;
      var value = unwrap(source);
      if (!value || typeof value !== 'object') return;
      var candidate = value.star_distribution || value.distribution;
      if (!candidate && value.bottomline) candidate = value.bottomline.star_distribution;
      if (!candidate && value.response && value.response.bottomline) {
        candidate = value.response.bottomline.star_distribution;
      }
      if (candidate && typeof candidate === 'object') distribution = candidate;
    });
    return distribution;
  }

  function sortReviews(reviews) {
    var dated = reviews.filter(function (review) {
      return !isNaN(Date.parse(review.date));
    });
    if (!dated.length) return reviews.slice();

    return reviews.slice().sort(function (a, b) {
      var aTime = Date.parse(a.date);
      var bTime = Date.parse(b.date);
      if (isNaN(aTime) && isNaN(bTime)) return 0;
      if (isNaN(aTime)) return 1;
      if (isNaN(bTime)) return -1;
      return bTime - aTime;
    });
  }

  function formatCount(value) {
    return Math.round(asNumber(value)).toLocaleString('en-US');
  }

  function formatDate(value) {
    if (!value) return '';
    var raw = String(value).trim();
    var dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    var date = dateOnly
      ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
      : new Date(raw);
    if (isNaN(date.getTime())) return raw;
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  function starRow(score, size) {
    var rating = Math.max(0, Math.min(5, asNumber(score)));
    var fill = rating * 20;
    var icon = '<svg class="ag-stars__icon" viewBox="0 0 24 24" focusable="false"><path d="M14.43 10 12 2 9.57 10 2 10l6.18 4.41L5.83 22 12 17.31 18.18 22l-2.35-7.59L22 10z"/></svg>';
    var icons = '';
    for (var i = 0; i < 5; i++) icons += icon;
    return ''
      + '<span class="ag-stars" style="--ag-star-size:' + size + 'px;--ag-star-fill:' + fill + '%;" aria-hidden="true">'
      + '<span class="ag-stars__row ag-stars__row--empty">' + icons + '</span>'
      + '<span class="ag-stars__clip"><span class="ag-stars__row ag-stars__row--fill">' + icons + '</span></span>'
      + '</span>';
  }

  function cardHtml(review) {
    var who = '';
    if (review.author) who += '<p class="ag-review__author"></p>';
    if (review.verified) who += '<p class="ag-review__verified">Verified buyer</p>';

    var photos = '';
    if (review.images.length) {
      photos = '<div class="ag-review__photos">' + review.images.map(function (src) {
        return '<a href="" target="_blank" rel="noopener"><img alt="Photo from this review"></a>';
      }).join('') + '</div>';
    }

    return ''
      + '<article class="ag-review">'
      + '<header class="ag-review__header"><div class="ag-review__who">' + who + '</div>'
      + (review.date ? '<time class="ag-review__date"></time>' : '')
      + '</header>'
      + (review.score ? '<div class="ag-review__stars">' + starRow(review.score, 13) + '<span class="screenreader"></span></div>' : '')
      + (review.title ? '<h3 class="ag-review__title"></h3>' : '')
      + (review.body ? '<p class="ag-review__body"></p>' : '')
      + photos
      + '</article>';
  }

  function fillCard(article, review) {
    var author = article.querySelector('.ag-review__author');
    if (author) author.textContent = review.author;

    var date = article.querySelector('.ag-review__date');
    if (date) {
      date.textContent = formatDate(review.date);
      date.setAttribute('datetime', review.date);
    }

    var score = article.querySelector('.ag-review__stars .screenreader');
    if (score) score.textContent = review.score + ' out of 5 stars';

    var title = article.querySelector('.ag-review__title');
    if (title) title.textContent = review.title;

    var body = article.querySelector('.ag-review__body');
    if (body) body.textContent = review.body;

    var images = article.querySelectorAll('.ag-review__photos img');
    var links = article.querySelectorAll('.ag-review__photos a');
    for (var i = 0; i < images.length; i++) {
      images[i].src = review.images[i];
      links[i].href = review.images[i];
    }
  }

  function renderDistribution(container, distribution) {
    if (!container || !distribution) return;
    var rows = [5, 4, 3, 2, 1];
    var sum = rows.reduce(function (count, star) {
      return count + asNumber(distribution[star] != null ? distribution[star] : distribution[String(star)]);
    }, 0);
    if (!sum) return;

    container.hidden = false;
    container.innerHTML = '';
    rows.forEach(function (star) {
      var count = asNumber(distribution[star] != null ? distribution[star] : distribution[String(star)]);
      var row = document.createElement('div');
      row.className = 'ag-reviews__dist-row';

      var label = document.createElement('span');
      label.textContent = star + ' star';
      var bar = document.createElement('span');
      bar.className = 'ag-reviews__bar';
      bar.setAttribute('role', 'presentation');
      var fill = document.createElement('span');
      fill.style.width = ((count / sum) * 100) + '%';
      bar.appendChild(fill);
      var tally = document.createElement('span');
      tally.textContent = formatCount(count);

      row.appendChild(label);
      row.appendChild(bar);
      row.appendChild(tally);
      container.appendChild(row);
    });
  }

  function renderPage(list, pager, reviews, page, scroll) {
    var pages = Math.max(1, Math.ceil(reviews.length / PAGE_SIZE));
    var current = Math.min(Math.max(page, 1), pages);
    var slice = reviews.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

    list.innerHTML = slice.map(cardHtml).join('');
    var articles = list.querySelectorAll('.ag-review');
    for (var i = 0; i < articles.length; i++) fillCard(articles[i], slice[i]);

    if (reviews.length <= PAGE_SIZE) {
      pager.hidden = true;
      pager.innerHTML = '';
      return;
    }

    pager.hidden = false;
    pager.innerHTML = ''
      + '<button type="button" class="ag-reviews__page" data-page="' + (current - 1) + '"' + (current === 1 ? ' disabled' : '') + '>Previous</button>'
      + '<span class="ag-reviews__page-status">' + current + ' / ' + pages + '</span>'
      + '<button type="button" class="ag-reviews__page" data-page="' + (current + 1) + '"' + (current === pages ? ' disabled' : '') + '>Next</button>';

    var buttons = pager.querySelectorAll('button');
    for (var b = 0; b < buttons.length; b++) {
      buttons[b].addEventListener('click', function (event) {
        renderPage(list, pager, reviews, asNumber(event.currentTarget.getAttribute('data-page')), true);
      });
    }

    if (scroll) {
      var section = document.getElementById('ag-reviews');
      if (section && section.scrollIntoView) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  function init(doc) {
    doc = doc || document;
    var dataNode = doc.getElementById('ag-reviews-data');
    var list = doc.getElementById('ag-reviews-list');
    if (!dataNode || !list) return;

    var data;
    try {
      data = JSON.parse(dataNode.textContent);
    } catch (error) {
      return;
    }

    var section = doc.getElementById('ag-reviews');
    var distribution = distributionFrom(data.sources);
    if (section) {
      renderDistribution(section.querySelector('.ag-reviews__distribution'), distribution);
    }

    var reviews = sortReviews(collectReviews(data.sources));
    if (!reviews.length) return;

    var pager = section ? section.querySelector('.ag-reviews__pager') : null;
    if (!pager) return;
    renderPage(list, pager, reviews, 1, false);
  }

  function suppressYotpo() {
    if (!document.getElementById('ag-reviews')) return;

    var selector = [
      '.yotpo-widget-instance[data-yotpo-instance-id="997376"]',
      '.yotpo-main-widget',
      '#yotpo-reviews-main-widget',
      '.yotpo-reviews-main-widget',
      '#yotpo-reviews-star-ratings-widget',
      '.yotpo-reviews-star-ratings-widget'
    ].join(',');

    function removeWidgets() {
      var nodes = document.querySelectorAll(selector);
      for (var i = 0; i < nodes.length; i++) nodes[i].remove();
    }

    removeWidgets();
    if (typeof MutationObserver === 'undefined') return;

    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      (window.requestAnimationFrame || function (fn) { return setTimeout(fn, 16); })(function () {
        scheduled = false;
        removeWidgets();
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(function () { observer.disconnect(); }, 12000);
  }

  return {
    init: init,
    suppressYotpo: suppressYotpo,
    collectReviews: collectReviews,
    distributionFrom: distributionFrom,
    normalizeReview: normalizeReview,
    reviewsFromHtml: reviewsFromHtml,
    sortReviews: sortReviews
  };
});
